import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import DescriptionRichEditor from "./DescriptionRichEditor";

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Value for `datetime-local` input */
export function toDatetimeLocalValue(d) {
  if (!d) return "";
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}T${pad2(x.getHours())}:${pad2(x.getMinutes())}`;
}

function fromDatetimeLocalValue(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDateOnlyValue(d) {
  if (!d) return "";
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
}

function fromDateOnlyValue(s) {
  if (!s) return null;
  const [y, m, day] = s.split("-").map(Number);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day, 12, 0, 0);
}

/* Trello-like neutrals (modal is always light for parity with reference) */
const tc = {
  text: "#172b4d",
  muted: "#5e6c84",
  subtle: "#626f86",
  border: "#dfe1e6",
  page: "#fff",
  sidebar: "#f1f2f4",
  btn: "rgba(9, 30, 66, 0.06)",
  btnHover: "rgba(9, 30, 66, 0.1)",
  fieldBg: "#f4f5f7",
  primary: "#0c66e4",
  primaryHover: "#0055cc",
  /** Typed characters in inputs (overrides global dark-theme input color). */
  inputText: "#000000",
};

function SidebarAction({ icon, label, onClick, active }) {
  return (
    <Button
      fullWidth
      startIcon={icon}
      onClick={onClick}
      sx={{
        justifyContent: "flex-start",
        textTransform: "none",
        fontWeight: 400,
        color: tc.text,
        bgcolor: active ? tc.btnHover : tc.btn,
        borderRadius: 1,
        py: 1,
        px: 1.5,
        mb: 0.5,
        "&:hover": { bgcolor: tc.btnHover },
      }}
    >
      {label}
    </Button>
  );
}

export default function EventCardDialog({ open, onClose, fcEvent, selectInfo }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [allDay, setAllDay] = useState(true);
  const [startVal, setStartVal] = useState("");
  const [endVal, setEndVal] = useState("");
  const [labels, setLabels] = useState([]);
  const [labelInput, setLabelInput] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [assigneeInput, setAssigneeInput] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [checkInput, setCheckInput] = useState("");
  const [attachmentNames, setAttachmentNames] = useState([]);
  const [activity, setActivity] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [saveError, setSaveError] = useState("");
  const [watching, setWatching] = useState(false);
  const [joined, setJoined] = useState(false);
  const [showActivityDetails, setShowActivityDetails] = useState(true);
  const [sidebarSection, setSidebarSection] = useState(null);

  const isEdit = Boolean(fcEvent);

  useEffect(() => {
    if (!open) return;
    setSaveError("");
    setSidebarSection("dates");

    if (fcEvent) {
      const xp = fcEvent.extendedProps || {};
      setTitle(fcEvent.title || "");
      setDescription(xp.description ?? "");
      setAllDay(Boolean(fcEvent.allDay));
      if (fcEvent.allDay) {
        setStartVal(toDateOnlyValue(fcEvent.start));
        const endExcl = fcEvent.end ? new Date(fcEvent.end) : null;
        if (endExcl) {
          const inclusive = new Date(endExcl);
          inclusive.setDate(inclusive.getDate() - 1);
          setEndVal(toDateOnlyValue(inclusive));
        } else {
          setEndVal(toDateOnlyValue(fcEvent.start));
        }
      } else {
        setStartVal(toDatetimeLocalValue(fcEvent.start));
        setEndVal(toDatetimeLocalValue(fcEvent.end || fcEvent.start));
      }
      setLabels(Array.isArray(xp.labels) ? [...xp.labels] : []);
      setAssignees(Array.isArray(xp.assignees) ? [...xp.assignees] : []);
      setChecklist(Array.isArray(xp.checklist) ? xp.checklist.map((c) => ({ ...c })) : []);
      setAttachmentNames(Array.isArray(xp.attachmentNames) ? [...xp.attachmentNames] : []);
      setActivity(Array.isArray(xp.activity) ? [...xp.activity] : []);
      setWatching(Boolean(xp.watching));
      setJoined(Boolean(xp.joined));
    } else if (selectInfo) {
      setTitle("");
      setDescription("");
      setAllDay(Boolean(selectInfo.allDay));
      if (selectInfo.allDay) {
        setStartVal(toDateOnlyValue(selectInfo.start));
        const endExcl = selectInfo.end ? new Date(selectInfo.end) : new Date(selectInfo.start);
        const inclusive = new Date(endExcl);
        inclusive.setDate(inclusive.getDate() - 1);
        setEndVal(toDateOnlyValue(inclusive));
      } else {
        setStartVal(toDatetimeLocalValue(selectInfo.start));
        setEndVal(toDatetimeLocalValue(selectInfo.end || selectInfo.start));
      }
      setLabels([]);
      setAssignees([]);
      setChecklist([]);
      setAttachmentNames([]);
      setActivity([{ at: new Date().toISOString(), text: "Card created" }]);
      setWatching(false);
      setJoined(false);
    }
    setLabelInput("");
    setAssigneeInput("");
    setCheckInput("");
    setCommentDraft("");
  }, [open, fcEvent, selectInfo]);

  const pickSection = (key) => {
    setSidebarSection((prev) => (prev === key ? null : key));
  };

  const initials = useMemo(() => {
    const s = title.trim() || "You";
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return s.slice(0, 2).toUpperCase() || "ME";
  }, [title]);

  const addLabel = () => {
    const v = labelInput.trim();
    if (!v || labels.includes(v)) return;
    setLabels([...labels, v]);
    setLabelInput("");
  };

  const addAssignee = () => {
    const v = assigneeInput.trim();
    if (!v || assignees.includes(v)) return;
    setAssignees([...assignees, v]);
    setAssigneeInput("");
  };

  const addChecklistItem = () => {
    const v = checkInput.trim();
    if (!v) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setChecklist([...checklist, { id, text: v, done: false }]);
    setCheckInput("");
  };

  const toggleCheck = (id) => {
    setChecklist((prev) => prev.map((c) => (c.id === id ? { ...c, done: !c.done } : c)));
  };

  const removeCheck = (id) => {
    setChecklist((prev) => prev.filter((c) => c.id !== id));
  };

  const onFiles = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const names = [...attachmentNames];
    for (let i = 0; i < files.length; i += 1) {
      names.push(files[i].name);
    }
    setAttachmentNames(names);
    e.target.value = "";
  };

  const addComment = () => {
    const v = commentDraft.trim();
    if (!v) return;
    setActivity((a) => [...a, { at: new Date().toISOString(), text: v }]);
    setCommentDraft("");
  };

  const buildDates = () => {
    if (allDay) {
      const s = fromDateOnlyValue(startVal);
      let e = fromDateOnlyValue(endVal);
      if (!s) return null;
      if (!e || e < s) e = new Date(s);
      const endExclusive = new Date(e);
      endExclusive.setDate(endExclusive.getDate() + 1);
      return { start: s, end: endExclusive, allDay: true };
    }
    const s = fromDatetimeLocalValue(startVal);
    const e = fromDatetimeLocalValue(endVal);
    if (!s) return null;
    const endOk = e && e > s ? e : new Date(s.getTime() + 60 * 60 * 1000);
    return { start: s, end: endOk, allDay: false };
  };

  const handleSave = () => {
    setSaveError("");
    const trimmed = title.trim() || "Untitled event";
    const dates = buildDates();
    if (!dates) {
      setSaveError("Please set valid start and end dates.");
      return;
    }

    const nextActivity = [
      ...activity,
      { at: new Date().toISOString(), text: isEdit ? "Card updated" : "Card saved" },
    ];

    const extendedProps = {
      description: description.trim(),
      labels,
      assignees,
      checklist,
      attachmentNames,
      activity: nextActivity,
      watching,
      joined,
    };

    onClose({
      action: "save",
      fcEvent,
      payload: {
        title: trimmed,
        start: dates.start,
        end: dates.end,
        allDay: dates.allDay,
        extendedProps,
      },
    });
  };

  const handleDelete = () => {
    if (!fcEvent) return;
    if (!window.confirm("Archive this card? It will be removed from the calendar.")) return;
    onClose({ action: "delete", fcEvent });
  };

  const handleClose = () => {
    onClose({ action: "cancel" });
  };

  const handleShare = async () => {
    const text = `${title || "Event"}\n${description || ""}`.trim();
    try {
      if (navigator.share) {
        await navigator.share({ title: title || "Event", text });
      } else {
        await navigator.clipboard?.writeText(text || "Event card");
      }
    } catch {
      /* user cancelled or clipboard failed */
    }
  };

  const handleCopy = async () => {
    const text = `${title || "Event"}\n${description || ""}`.trim();
    try {
      await navigator.clipboard?.writeText(text || "Event card");
    } catch {
      /* ignore */
    }
  };

  const footerBorder = `1px solid ${tc.border}`;

  const datesPanel = (
    <Box sx={{ pt: 1 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            size="small"
            sx={{ color: tc.muted }}
          />
        }
        label={<Typography variant="body2">All day</Typography>}
      />
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        <TextField
          label={allDay ? "Start date" : "Start"}
          type={allDay ? "date" : "datetime-local"}
          size="small"
          fullWidth
          value={startVal}
          onChange={(e) => setStartVal(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: tc.page } }}
        />
        <TextField
          label={allDay ? "End date" : "End"}
          type={allDay ? "date" : "datetime-local"}
          size="small"
          fullWidth
          value={endVal}
          onChange={(e) => setEndVal(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: tc.page } }}
        />
      </Stack>
    </Box>
  );

  const membersPanel = (
    <Box sx={{ pt: 1 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {assignees.map((a) => (
          <Chip
            key={a}
            label={a}
            size="small"
            onDelete={() => setAssignees(assignees.filter((x) => x !== a))}
            sx={{ bgcolor: "#dfe1e6", color: tc.text }}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          placeholder="Name or email"
          value={assigneeInput}
          onChange={(e) => setAssigneeInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAssignee())}
          fullWidth
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: tc.page } }}
        />
        <IconButton onClick={addAssignee} size="small" sx={{ border: `1px solid ${tc.border}` }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );

  const labelsPanel = (
    <Box sx={{ pt: 1 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {labels.map((lab) => (
          <Chip
            key={lab}
            label={lab}
            size="small"
            onDelete={() => setLabels(labels.filter((l) => l !== lab))}
            sx={{ bgcolor: "#579dff", color: "#fff" }}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          placeholder="Add label"
          value={labelInput}
          onChange={(e) => setLabelInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLabel())}
          fullWidth
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: tc.page } }}
        />
        <IconButton onClick={addLabel} size="small" sx={{ border: `1px solid ${tc.border}` }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );

  const checklistPanel = (
    <Box sx={{ pt: 1 }}>
      <Stack spacing={1} sx={{ mb: 1 }}>
        {checklist.map((item) => (
          <Stack key={item.id} direction="row" alignItems="center" spacing={1}>
            <Checkbox size="small" checked={item.done} onChange={() => toggleCheck(item.id)} sx={{ p: 0.5 }} />
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                color: tc.text,
                textDecoration: item.done ? "line-through" : "none",
                opacity: item.done ? 0.65 : 1,
              }}
            >
              {item.text}
            </Typography>
            <IconButton size="small" aria-label="Remove" onClick={() => removeCheck(item.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          placeholder="Add an item"
          value={checkInput}
          onChange={(e) => setCheckInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChecklistItem())}
          fullWidth
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: tc.page } }}
        />
        <IconButton onClick={addChecklistItem} size="small" sx={{ border: `1px solid ${tc.border}` }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );

  const attachmentPanel = (
    <Box sx={{ pt: 1 }}>
      <Button
        variant="outlined"
        component="label"
        size="small"
        startIcon={<AttachFileIcon />}
        sx={{ textTransform: "none", color: tc.text, borderColor: tc.border }}
      >
        Attach files
        <input type="file" multiple hidden onChange={onFiles} />
      </Button>
      {attachmentNames.length > 0 ? (
        <Stack component="ul" sx={{ m: 1, pl: 2, mt: 1 }} spacing={0.5}>
          {attachmentNames.map((n, i) => (
            <Typography key={`${n}-${i}`} component="li" variant="caption" sx={{ color: tc.muted }}>
              {n}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: tc.muted }}>
          Names are stored on this card for this session.
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: "12px",
          backgroundColor: tc.page,
          color: tc.text,
          maxHeight: "94vh",
          overflow: "hidden",
          "& .MuiOutlinedInput-input, & .MuiInputBase-input, & textarea.MuiOutlinedInput-input": {
            color: tc.inputText,
            WebkitTextFillColor: tc.inputText,
            caretColor: tc.inputText,
          },
          "& input:not([type='checkbox']):not([type='radio']):not([type='file']):not([type='hidden']), & textarea": {
            color: tc.inputText + " !important",
            WebkitTextFillColor: tc.inputText + " !important",
            caretColor: tc.inputText,
          },
          "& .MuiOutlinedInput-input::placeholder, & .MuiInputBase-input::placeholder, & textarea::placeholder": {
            color: "#6b778c",
            opacity: 1,
          },
          "& .MuiInputLabel-root:not(.Mui-error):not(.Mui-disabled)": {
            color: tc.muted,
          },
          "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": {
            color: tc.primary,
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "94vh" }}>
        {/* Header row — title + close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            px: 3,
            pt: 2.5,
            pb: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
            <TextField
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              multiline
              minRows={1}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: tc.inputText,
                  lineHeight: 1.3,
                  "& .MuiInputBase-input": {
                    color: tc.inputText,
                    WebkitTextFillColor: tc.inputText,
                    caretColor: tc.inputText,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#6b778c",
                    opacity: 1,
                  },
                },
              }}
            />
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, cursor: "default" }}>
              <Typography variant="body2" sx={{ color: tc.muted }}>
                in list
              </Typography>
              <Typography variant="body2" sx={{ color: tc.text, fontWeight: 500 }}>
                Calendar
              </Typography>
              <ExpandMoreIcon sx={{ fontSize: 18, color: tc.muted }} />
            </Stack>
          </Box>
          <IconButton aria-label="Close" onClick={handleClose} size="small" sx={{ color: tc.muted, mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden", flexDirection: { xs: "column", sm: "row" } }}>
          {/* Main column */}
          <Box
            sx={{
              flex: "1 1 62%",
              px: 3,
              pb: 2,
              overflowY: "auto",
              borderRight: { xs: "none", sm: `1px solid ${tc.border}` },
              borderBottom: { xs: `1px solid ${tc.border}`, sm: "none" },
            }}
          >
            {saveError ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError("")}>
                {saveError}
              </Alert>
            ) : null}

            <Button
              startIcon={<VisibilityOutlinedIcon />}
              onClick={() => setWatching((w) => !w)}
              sx={{
                textTransform: "none",
                color: watching ? tc.primary : tc.text,
                fontWeight: 400,
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                mb: 2,
                bgcolor: watching ? "rgba(0, 101, 255, 0.08)" : tc.btn,
                "&:hover": { bgcolor: watching ? "rgba(0, 101, 255, 0.12)" : tc.btnHover },
              }}
            >
              {watching ? "Watching" : "Watch"}
            </Button>

            {/* Description — Trello-style rich editor */}
            <Box sx={{ pl: { xs: 0, sm: 0 } }}>
              <DescriptionRichEditor
                value={description}
                onCommit={setDescription}
                resetKey={open ? `${fcEvent?.id || "new"}-${selectInfo?.startStr || ""}` : ""}
              />
            </Box>

            <Divider sx={{ my: 3, borderColor: tc.border }} />

            {/* Activity */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: tc.muted }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: tc.text }}>
                  Activity
                </Typography>
              </Stack>
              <Button
                size="small"
                onClick={() => setShowActivityDetails((v) => !v)}
                sx={{ textTransform: "none", color: tc.muted, fontSize: "0.75rem", minWidth: 0, p: 0.5 }}
              >
                {showActivityDetails ? "Hide details" : "Show details"}
              </Button>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "#ff991f",
                  color: "#fff",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {initials}
              </Box>
              <TextField
                multiline
                maxRows={4}
                fullWidth
                size="small"
                placeholder="Write a comment…"
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    addComment();
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: tc.fieldBg,
                    "& fieldset": { borderColor: "transparent" },
                  },
                }}
              />
            </Stack>

            <Box sx={{ pl: { xs: 0, sm: 5.5 }, maxHeight: 220, overflowY: "auto" }}>
              {activity.map((row, i) => (
                <Box key={`${row.at}-${i}`} sx={{ mb: 2, display: "flex", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: i % 2 ? "#579dff" : "#6cc3e0",
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    {showActivityDetails ? (
                      <Typography variant="caption" sx={{ color: tc.muted, display: "block" }}>
                        {new Date(row.at).toLocaleString()}
                      </Typography>
                    ) : null}
                    <Typography variant="body2" sx={{ color: tc.text, lineHeight: 1.45 }}>
                      {row.text}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Sidebar */}
          <Box
            sx={{
              flex: { xs: "1 1 auto", sm: "0 0 220px", md: "0 0 256px" },
              bgcolor: tc.sidebar,
              px: 1.5,
              py: 2,
              overflowY: "auto",
              maxHeight: { xs: 360, sm: "none" },
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: tc.muted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", pl: 0.5 }}
            >
              Add to card
            </Typography>
            <Box sx={{ mt: 1 }}>
              <SidebarAction
                icon={<PersonAddOutlinedIcon fontSize="small" />}
                label={joined ? "Leave" : "Join"}
                active={joined}
                onClick={() => setJoined((j) => !j)}
              />
              <SidebarAction
                icon={<PersonOutlineIcon fontSize="small" />}
                label="Members"
                active={sidebarSection === "members"}
                onClick={() => pickSection("members")}
              />
              <SidebarAction
                icon={<LabelOutlinedIcon fontSize="small" />}
                label="Labels"
                active={sidebarSection === "labels"}
                onClick={() => pickSection("labels")}
              />
              <SidebarAction
                icon={<CheckBoxOutlinedIcon fontSize="small" />}
                label="Checklist"
                active={sidebarSection === "checklist"}
                onClick={() => pickSection("checklist")}
              />
              <SidebarAction
                icon={<AccessTimeIcon fontSize="small" />}
                label="Dates"
                active={sidebarSection === "dates"}
                onClick={() => pickSection("dates")}
              />
              <SidebarAction
                icon={<AttachFileIcon fontSize="small" />}
                label="Attachment"
                active={sidebarSection === "attachment"}
                onClick={() => pickSection("attachment")}
              />
            </Box>

            {sidebarSection ? (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: tc.page,
                  border: `1px solid ${tc.border}`,
                }}
              >
                {sidebarSection === "dates" && datesPanel}
                {sidebarSection === "members" && membersPanel}
                {sidebarSection === "labels" && labelsPanel}
                {sidebarSection === "checklist" && checklistPanel}
                {sidebarSection === "attachment" && attachmentPanel}
              </Box>
            ) : null}

            <Divider sx={{ my: 2, borderColor: tc.border }} />

            <Typography
              variant="caption"
              sx={{ color: tc.muted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", pl: 0.5 }}
            >
              Actions
            </Typography>
            <Box sx={{ mt: 1 }}>
              <SidebarAction icon={<ContentCopyOutlinedIcon fontSize="small" />} label="Copy" onClick={handleCopy} />
              <SidebarAction icon={<ShareOutlinedIcon fontSize="small" />} label="Share" onClick={handleShare} />
              {isEdit ? (
                <SidebarAction
                  icon={<Inventory2OutlinedIcon fontSize="small" />}
                  label="Archive"
                  onClick={handleDelete}
                />
              ) : null}
            </Box>
          </Box>
        </Box>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: footerBorder,
            bgcolor: tc.page,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button onClick={handleClose} sx={{ textTransform: "none", color: tc.text }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: "none",
              bgcolor: tc.primary,
              boxShadow: "none",
              "&:hover": { bgcolor: tc.primaryHover, boxShadow: "none" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
